import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { gearChecklistAPI } from '../utils/api';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const ConfirmGear = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Missing confirmation token. Please use the link from your email or SMS.');
      return;
    }
    gearChecklistAPI
      .confirmPublic(token)
      .then((res) => {
        setResult(res.data);
        setStatus('success');
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'We could not confirm your gear. The link may have expired.');
        setStatus('error');
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="confirm-gear-page">
      <div className="w-full max-w-md card p-8 text-center">
        <h1 className="text-2xl sm:text-3xl font-serif mb-2">Pre-event Checklist</h1>
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-6">Gear Confirmation</p>

        {status === 'loading' && (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="confirm-gear-loading">
            <Loader2 size={36} className="animate-spin text-gold" />
            <p className="text-sm text-muted-foreground">Confirming your readiness…</p>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center gap-3 py-4" data-testid="confirm-gear-success">
            <CheckCircle2 size={56} className="text-green-600 dark:text-green-400" />
            <h2 className="text-xl font-medium">You're confirmed!</h2>
            {result?.event_name && (
              <p className="text-sm text-muted-foreground">
                Gear confirmed for <span className="font-medium text-foreground">{result.event_name}</span>.
              </p>
            )}
            <p className="text-sm text-muted-foreground mt-2">
              Thanks{result?.crew_member_name ? `, ${result.crew_member_name.split(' ')[0]}` : ''}. The team has been notified.
            </p>
            <Link
              to="/login"
              className="mt-6 inline-block bg-primary text-primary-foreground px-6 py-2.5 text-sm font-medium hover:bg-black/90"
              data-testid="confirm-gear-open-portal"
            >
              OPEN CREW PORTAL
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center gap-3 py-4" data-testid="confirm-gear-error">
            <XCircle size={56} className="text-red-500" />
            <h2 className="text-xl font-medium">Couldn't confirm</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Link
              to="/login"
              className="mt-6 inline-block border border-border px-6 py-2.5 text-sm hover:bg-accent"
              data-testid="confirm-gear-back-login"
            >
              GO TO LOGIN
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmGear;
